/**
 * share — the wall's Share door, as pure decisions (plan `2026_10_25_wall_share.md`). What the menu
 * offers, what a tap does, what the native sheet is handed, and what a refusal is called. No DOM
 * type is touched at runtime: `useShare.ts` wires the browser, the specs drive these with plain values.
 */
import type {ShareWords} from './ui/theme'

export type ShareMethod = 'native' | 'download' | 'copy_image' | 'copy_link'

export type ShareFailReason = 'cancelled' | 'not_allowed' | 'unsupported' | 'fetch' | 'clipboard' | 'unknown'

/** A service-generated take id: the name of the public picture (`<uuid>.webp`) and of its take page. */
export const TAKE_KEY = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** `<uuid>.webp` — the key `/generations/:key` serves, built from `TAKE_KEY` so the shape is stated once. */
export const TAKE_IMAGE_KEY = new RegExp(`${TAKE_KEY.source.slice(0, -1)}\\.webp$`, 'i')

/** The take page's path for a take's `image_key`; null for a key that is not a take's picture. */
export function takeSharePath(imageKey: string): string | null {
    if (!TAKE_IMAGE_KEY.test(imageKey)) return null
    return `/take/${imageKey.slice(0, -'.webp'.length)}`
}

/** The lines the door says through the wall's Notice. */
export const SHARE_LINES = {
    linkCopied: 'link copied',
    pictureCopied: 'picture copied',
    failed: 'could not share',
    copyFailed: 'could not copy',
} as const

export const SHARE_SUCCESS_LINES: ReadonlySet<string> = new Set([SHARE_LINES.linkCopied, SHARE_LINES.pictureCopied])
export const SHARE_ERROR_LINES: ReadonlySet<string> = new Set([SHARE_LINES.failed, SHARE_LINES.copyFailed])

/** What this browser can do, probed client-side after mount. `canShareFile` implies `canShare`. */
export type ShareEnv = {canShare: boolean; canShareFile: boolean; canCopyImage: boolean}

/** The menu's rows, in order. A house without a link never offers `copy_link`; the system sheet is
 *  offered only where it can carry something (the file, or the link). */
export function shareMethods(env: ShareEnv, link: boolean): ShareMethod[] {
    const methods: ShareMethod[] = []
    if (env.canShare && (link || env.canShareFile)) methods.push('native')
    methods.push('download')
    if (env.canCopyImage) methods.push('copy_image')
    if (link) methods.push('copy_link')
    return methods
}

/**
 * The menu for the reader at the door (plan `2026_10_25_toile_first_take_free.md` § 7, operator 2026-10-25).
 * A member gets the house's menu. A visitor holding a free take gets the keep-only methods - download, copy
 * picture, the system sheet with the file alone, as a house with no link - and, where the house passes links on,
 * a `copy_link` row that opens the sign-in card: a link needs an account, the take page resolves only for one.
 */
export function doorMethods(env: ShareEnv, link: boolean, signedIn: boolean): ShareMethod[] {
    if (signedIn) return shareMethods(env, link)
    return link ? [...shareMethods(env, false), 'copy_link'] : shareMethods(env, false)
}

/** A visitor's pick that needs an account instead of running: the link. */
export function pickOpensSignIn(method: ShareMethod, signedIn: boolean): boolean {
    return !signedIn && method === 'copy_link'
}

/** A tap on the door: the system sheet at once on the phone's sheet when it can carry something,
 *  the menu everywhere else. */
export function tapAction(env: ShareEnv, surface: 'column' | 'sheet', link: boolean): 'native' | 'menu' {
    return surface === 'sheet' && shareMethods(env, link).includes('native') ? 'native' : 'menu'
}

/** The link rides inside `text` (several targets drop `url` when a file is attached) at the words'
 *  `{url}` mark. */
export const SHARE_URL_MARK = '{url}'

/** What `navigator.share` is handed. With a file: the file, the title and, where the house has
 *  words and a link, the text carrying it — no `url`. Without: the title, the text and the `url`.
 *  An empty title is left out (Ovid's file travels alone). */
export function shareData(
    file: File | null, link: string | null, words: Pick<ShareWords, 'title' | 'text'>,
): ShareData {
    const text = words.text !== null && link !== null ? words.text.replace(SHARE_URL_MARK, link) : null
    const data: ShareData = {}
    if (file) data.files = [file]
    if (words.title !== '') data.title = words.title
    if (text !== null) data.text = text
    if (!file && link !== null) data.url = link
    return data
}

/** A refusal's name, for the analytics `reason` and for whether the reader is told. */
export function failReasonFor(err: unknown): ShareFailReason {
    const name = typeof err === 'object' && err !== null && 'name' in err ? String((err as {name: unknown}).name) : ''
    if (name === 'AbortError') return 'cancelled'
    if (name === 'NotAllowedError') return 'not_allowed'
    if (name === 'TypeError' || name === 'NotSupportedError') return 'unsupported'
    return 'unknown'
}
